import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  alpha,
  useTheme,
  Tooltip,
} from "@mui/material";
import { Plus, Trash2, RefreshCw } from "lucide-react";
import {
  fetchMarketingScoringRules,
  createMarketingScoringRule,
  deleteMarketingScoringRule,
  recalculateMarketingScores,
} from "../../../../../../redux/actions/marketingAction";
import DeleteConfirmDialog from "../DeleteConfirmDialog";

const ScoringRules = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { scoringRules } = useSelector((state) => state.marketing);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [formData, setFormData] = useState({
    event_type: "", points: 0, description: "", is_active: true,
  });

  useEffect(() => {
    dispatch(fetchMarketingScoringRules());
  }, [dispatch]);

  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      await dispatch(createMarketingScoringRule(formData));
      setFormOpen(false);
      setFormData({ event_type: "", points: 0, description: "", is_active: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const result = await dispatch(recalculateMarketingScores());
      alert(`Recalculated scores for ${result?.updated || 0} contacts.`);
    } finally {
      setRecalculating(false);
    }
  };

  const items = scoringRules || [];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {t('mkt_lead_scoring_rules')} ({items.length})
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshCw size={16} className={recalculating ? "animate-spin" : ""} />}
            onClick={handleRecalculate}
            disabled={recalculating}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
          >
            {t('mkt_recalculate_all')}
          </Button>
          <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => setFormOpen(true)} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}>
            {t('mkt_new_rule')}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.divider, 0.3) }}>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{t('mkt_event_type')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{t('mkt_points')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{t('mkt_description')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{t('mkt_status')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{t('mkt_actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: theme.palette.text.secondary }}>{t('mkt_no_scoring_rules')}</TableCell></TableRow>
              ) : items.map((rule) => (
                <TableRow key={rule.id} hover>
                  <TableCell>
                    <Chip label={rule.event_type.replace(/_/g, " ")} size="small" sx={{ borderRadius: "6px", fontSize: "0.75rem", fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: rule.points > 0 ? "#10b981" : theme.palette.error?.main,
                      }}
                    >
                      {rule.points > 0 ? "+" : ""}{rule.points}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{rule.description || "—"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rule.is_active ? t('mkt_active') : t('mkt_inactive')}
                      size="small"
                      color={rule.is_active ? "success" : "default"}
                      sx={{ borderRadius: "6px", fontSize: "0.7rem" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteTarget(rule)}>
                        <Trash2 size={14} color={theme.palette.error?.main} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{t('mkt_new_scoring_rule')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField label={t('mkt_event_type')} value={formData.event_type} onChange={(e) => setFormData({ ...formData, event_type: e.target.value })} fullWidth size="small" required placeholder={t('mkt_event_type_placeholder')} />
            <TextField label={t('mkt_points')} type="number" value={formData.points} onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })} fullWidth size="small" required />
            <TextField label={t('mkt_description')} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth size="small" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)}>{t('common_cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!formData.event_type || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            {submitting ? t('mkt_creating') : t('mkt_create')}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await dispatch(deleteMarketingScoringRule(deleteTarget.id));
          setDeleteTarget(null);
        }}
        itemName={deleteTarget?.event_type}
        itemType={t("mkt_rule")}
      />
    </Box>
  );
};

export default ScoringRules;
